import {
	Modal,
	Stack,
	Text,
	Alert,
	Button,
	Group,
	Card,
	Progress,
	Divider,
	Anchor,
} from "@mantine/core";
import {
	IconShieldExclamation,
	IconDeviceMobile,
	IconMessage,
	IconArrowLeft,
} from "@tabler/icons-react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { getCurrentUserQueryOptions } from "@/users/operations/get-current-user/getCurrentUserQueryOptions";
import type { GetCurrentUserOutputType } from "@/users/operations/get-current-user/GetCurrentUserOutput";
import { useUserMFAStatus } from "../hooks/useUserMFAStatus";
import { usePendingTOTPSecret } from "../hooks/usePendingTOTPSecret";
import { TOTPStatus } from "./TOTPStatus";
import { useEffect, useState } from "react";
import { UserRole } from "@/users/models/UserRole";
import { notifications } from "@mantine/notifications";
import { useBreakpoint } from "@/common/hooks/useBreakpoint";
import { updateUserMFAPreferenceMutationOptions } from "./updateUserMFAPreferenceMutationOptions";
import { useAmplifyAuth } from "./AmplifyAuthContext";
import { useFreshAuth, type FreshAuth } from "../hooks/useFreshAuth";
import { useLastKnownMFAStatus } from "../hooks/useLastKnownMFAStatus";
import { FreshAuthRequired } from "./FreshAuthRequired";

type MFAChoice = "SMS_MFA" | "SOFTWARE_TOKEN_MFA" | null;

export function MFAEnforcementModal() {
	const currentUserQuery = useQuery(getCurrentUserQueryOptions());
	const mfaStatus = useUserMFAStatus();
	const freshAuth = useFreshAuth();
	const lastKnownMFAStatus = useLastKnownMFAStatus(currentUserQuery.data?.id);

	const hasMFA = mfaStatus.hasTOTP || mfaStatus.hasSMS;
	const hasReadMFAStatus =
		!mfaStatus.isLoading && !mfaStatus.needsFreshAuth && !mfaStatus.error;
	// A stale token cannot read MFA status. Only block users this device has
	// already seen without MFA, so users with MFA are not forced to sign in again.
	const needsFreshAuth =
		mfaStatus.needsFreshAuth && lastKnownMFAStatus.hasMFA === false;

	// Check if user needs MFA enforcement - only for "normal" users (not clients or trusted contacts)
	const needsMFAEnforcement =
		currentUserQuery.data &&
		currentUserQuery.data.role !== UserRole.Client &&
		currentUserQuery.data.role !== UserRole.TrustedContact &&
		// don't enforce MFA for app store review account
		currentUserQuery.data.email !==
			import.meta.env.VITE_APP_STORE_REVIEW_CAREGIVER_EMAIL &&
		(needsFreshAuth || (hasReadMFAStatus && !hasMFA));

	useEffect(() => {
		if (hasReadMFAStatus && !lastKnownMFAStatus.isLoading) {
			lastKnownMFAStatus.save(hasMFA);
		}
	}, [hasReadMFAStatus, hasMFA, lastKnownMFAStatus.isLoading]);

	if (!needsMFAEnforcement || !currentUserQuery.data) {
		return null;
	}

	// Mounted only while enforcement is needed, so it opens as soon as the
	// userMFAStatus query reports no MFA (e.g. after MFA is disabled) and its
	// step state resets every time it reopens.
	return (
		<MFAEnforcementModalContent
			phoneNumber={currentUserQuery.data.phoneNumber}
			needsFreshAuth={needsFreshAuth}
			ensureFreshAuth={freshAuth.ensureFreshAuth}
		/>
	);
}

interface MFAEnforcementModalContentProps {
	phoneNumber: GetCurrentUserOutputType["phoneNumber"];
	needsFreshAuth: boolean;
	ensureFreshAuth: FreshAuth["ensureFreshAuth"];
}

function MFAEnforcementModalContent({
	phoneNumber,
	needsFreshAuth,
	ensureFreshAuth,
}: MFAEnforcementModalContentProps) {
	const queryClient = useQueryClient();
	const [selectedMFAType, setSelectedMFAType] = useState<MFAChoice>(null);
	const lessThanLg = useBreakpoint("lg");
	const { updateMFAPreference } = useAmplifyAuth();
	const { restoredSecretKey } = usePendingTOTPSecret();

	useEffect(() => {
		if (restoredSecretKey) {
			setSelectedMFAType("SOFTWARE_TOKEN_MFA");
		}
	}, [restoredSecretKey]);

	const mutation = updateUserMFAPreferenceMutationOptions(
		queryClient,
		updateMFAPreference,
	);
	const updateUserMFAPreferenceMutation = useMutation({
		...mutation,
		onSuccess: (data, variables, context) => {
			mutation.onSuccess?.(data, variables, context);
			notifications.show({
				title: "MFA Setup Complete",
				message: "SMS MFA has been enabled for your account",
				color: "green",
			});
		},
		onError: (error: Error) => {
			notifications.show({
				title: "Error setting up MFA",
				message: error.message,
				color: "red",
			});
		},
	});

	const handleMFASetupComplete = () => {
		queryClient.invalidateQueries({
			queryKey: ["userMFAStatus"],
		});
	};

	const isOnSetupStep = selectedMFAType !== null;
	const progressValue = isOnSetupStep ? 100 : 50;

	const getStepText = () => {
		if (!isOnSetupStep) return "Choose Method";
		if (selectedMFAType === "SMS_MFA") return "SMS Setup";
		if (selectedMFAType === "SOFTWARE_TOKEN_MFA") return "Authenticator Setup";
		return "Setup";
	};

	return (
		<Modal
			opened={true}
			onClose={() => {}} // Prevent closing by clicking outside or escape
			title="Security Setup Required"
			size="lg"
			closeOnClickOutside={false}
			closeOnEscape={false}
			withCloseButton={false}
			styles={{
				content: {
					paddingBottom: "var(--safe-area-inset-bottom)",
					paddingLeft: "var(--safe-area-inset-left)",
					paddingRight: "var(--safe-area-inset-right)",
					backgroundColor: "var(--mantine-color-body)",
				},
			}}
			centered
		>
			<Stack gap="lg">
				{/* Progress indicator */}
				<Stack gap="xs">
					<Group justify="space-between" align="center">
						<Text size="sm" fw={500} c="dimmed">
							{getStepText()}
						</Text>
						<Text size="xs" c="dimmed">
							Step {isOnSetupStep ? 2 : 1} of 2
						</Text>
					</Group>
					<Progress value={progressValue} size="sm" />
				</Stack>

				<Alert
					icon={<IconShieldExclamation size={20} />}
					color="orange"
					variant="light"
				>
					<Text size="xs" fw={500}>
						Multi-Factor Authentication Required
					</Text>
					<Text size="xs" mt="xs">
						For your account security, you must set up two-factor authentication
						before continuing to use the system.
					</Text>
				</Alert>

				{needsFreshAuth && <FreshAuthRequired />}

				{/* Main content area with consistent height */}
				<Stack gap="md" mih={240} display={needsFreshAuth ? "none" : undefined}>
					{selectedMFAType === null && (
						<Stack gap="md">
							<Text size="sm" fw={500}>
								Choose your preferred MFA method:
							</Text>

							<Group grow align="stretch">
								<Card
									withBorder
									p="md"
									style={{ cursor: "pointer" }}
									onClick={() => setSelectedMFAType("SMS_MFA")}
								>
									<Stack align="center" gap="sm">
										<IconMessage size={32} />
										<Text size="sm" fw={500} ta="center">
											SMS Text Message
										</Text>
										<Text size="xs" c="dimmed" ta="center">
											Receive codes via text message to your phone.
										</Text>
									</Stack>
								</Card>

								<Card
									withBorder
									p="md"
									style={{ cursor: "pointer" }}
									onClick={() => setSelectedMFAType("SOFTWARE_TOKEN_MFA")}
								>
									<Stack align="center" gap="sm">
										<IconDeviceMobile size={32} />
										<Text size="sm" fw={500} ta="center">
											Authenticator App
										</Text>
										<Text size="xs" c="dimmed" ta="center">
											Use an app like Authenticator App, Google Authenticator,
											or other authenticator apps.
										</Text>
									</Stack>
								</Card>
							</Group>
						</Stack>
					)}

					{selectedMFAType === "SMS_MFA" && (
						<Stack gap="md">
							<Text size="sm" fw={500}>
								Setting up SMS MFA
							</Text>
							{phoneNumber && (
								<Alert color="blue" variant="light">
									<Text size="sm">
										<strong>Phone Number:</strong> {phoneNumber}
									</Text>
								</Alert>
							)}
							<Text size="xs" c="dimmed">
								By clicking <strong>Enable SMS MFA</strong>, you agree to
								Village Caregiving's{" "}
								<Anchor
									href="/vcg-mobile-app-terms-of-service.html"
									target="_blank"
									size="xs"
									tabIndex={-1}
								>
									Terms of Service
								</Anchor>{" "}
								and{" "}
								<Anchor
									href="/vcg-mobile-app-privacy-policy.html"
									target="_blank"
									size="xs"
									tabIndex={-1}
								>
									Privacy Policy
								</Anchor>
								. You consent to receive SMS messages from Village Caregiving at
								your number on file to provide important information and
								verification codes. Message frequency depends on your activity.
								You may opt out by texting STOP. Text HELP for support. Message
								and data rates may apply.
							</Text>
						</Stack>
					)}

					{selectedMFAType === "SOFTWARE_TOKEN_MFA" && (
						<Stack gap="md">
							<Text size="sm" fw={500}>
								Setting up Authenticator App
							</Text>
							<TOTPStatus
								onRefresh={handleMFASetupComplete}
								setupAuthenticatorAppModalSize="lg"
							/>
						</Stack>
					)}
				</Stack>

				<Divider />

				{/* Consistent footer with navigation */}
				<Group justify="space-between" align="center">
					<Group>
						{isOnSetupStep && (
							<Button
								variant="subtle"
								leftSection={<IconArrowLeft size={16} />}
								onClick={() => setSelectedMFAType(null)}
								disabled={
									selectedMFAType === "SMS_MFA" &&
									updateUserMFAPreferenceMutation.isPending
								}
							>
								Back to Methods
							</Button>
						)}
					</Group>

					<Group>
						{selectedMFAType === "SMS_MFA" && (
							<Button
								onClick={async () => {
									if (!(await ensureFreshAuth())) {
										return;
									}
									updateUserMFAPreferenceMutation.mutate({
										totp: "DISABLED",
										email: "DISABLED",
										sms: "PREFERRED",
									});
								}}
								loading={updateUserMFAPreferenceMutation.isPending}
							>
								Enable SMS MFA
							</Button>
						)}
					</Group>
				</Group>

				<Text size="xs" c="dimmed" ta="center">
					This security measure helps protect your account and sensitive
					information.
				</Text>
			</Stack>
		</Modal>
	);
}
